<template>
  <Teleport to="body">
    <div
      id="loadingPopup"
      class="popup"
      :class="props.overlay_class"
      :style="[cloakStyle, props.shown ? props.popupStyle : {}]"
    >
      <div
        class="body popup-body"
        :class="props.body_class"
        :style="windowStyle"
        v-on-click-outside="onClickOutsideHandler"
      >
        <font-awesome-icon
          v-if="!noclose"
          class="closeBt"
          style="cursor: pointer"
          @click.prevent="close"
          :icon="['fa', 'circle-xmark']"
        />
        <slot></slot>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, CSSProperties } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import { useMagicKeys, whenever } from "@vueuse/core";
import { logicAnd } from "@vueuse/math";

const props = withDefaults(
  defineProps<{
    max_width?: string;
    max_height?: string;
    min_width?: string;
    min_height?: string;
    width?: string;
    height?: string;
    overflow?: string;
    align?: string;
    top?: string;
    shown?: boolean;
    noclose?: boolean;
    window_style?: CSSProperties;
    popupStyle?: CSSProperties;
    disable_escape_close?: boolean;
    /** When true, outside clicks do not close the popup (alias for callers using this name). */
    no_auto_close?: boolean;
    /** When true, Escape does not close the popup (alias of disable_escape_close for some parents). */
    no_escape?: boolean;
    /** Optional class(es) on the fullscreen overlay (e.g. for enter animations). */
    overlay_class?: string;
    /** Optional class(es) on the modal panel (e.g. for enter animations). */
    body_class?: string;
  }>(),
  {
    shown: true,
    popupStyle: () => ({
      position: "fixed",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      top: "0",
      left: "0",
      margin: "0",
      padding: "0",
    }),
    overlay_class: "",
    body_class: "",
  }
);

const emit = defineEmits(["close"]);

const close = () => {
  emit("close");
};

const onClickOutsideHandler: [(evt: any) => void, {}] = [
  (evt: any) => {
    if (!props.shown || props.no_auto_close) return;
    close();
  },
  { ignore: ["[class*=vc]", "[class*=popup-body],[class*=results],[id=NewFilterId]"] },
];

const keys = useMagicKeys();

const escapeEnabled = computed(
  () => !props.disable_escape_close && !props.no_escape
);

whenever(logicAnd(keys.Escape, escapeEnabled), () => {
  close();
});

const cloakStyle = computed(() => {
  return props.shown ? {} : { display: "none", opacity: 1 };
});

const windowStyle = computed(() => {
  let st = {
    "text-align": props.align ?? "center",
    "max-width": props.max_width,
    "min-width": props.min_width,
    "min-height": props.min_height,
    "max-height": props.max_height ?? `calc(100vh - ${props.top ?? "10rem"})`,
    top: props.top,
    width: props.width,
    height: props.height,
    ...props.window_style,
  } as any;

  // Ensure width respects viewport if specified (only for pixel/unit values, not percentages)
  if (props.width && !props.max_width && !props.width.includes("%")) {
    const widthValue = parseFloat(props.width);
    if (!isNaN(widthValue) && widthValue > 0) {
      st["max-width"] = `min(${props.width}, calc(100vw - 2rem))`;
    }
  }

  // Ensure height respects viewport if specified (only for pixel/unit values, not percentages)
  if (props.height && !props.max_height && !props.height.includes("%")) {
    const heightValue = parseFloat(props.height);
    if (!isNaN(heightValue) && heightValue > 0) {
      st["max-height"] = `min(${props.height}, calc(100vh - 2rem))`;
    }
  }

  if (props.overflow) st.overflow = props.overflow;
  Object.keys(st).forEach((key) => (st[key] === undefined ? delete st[key] : {}));
  return st;
});
</script>

<style scoped>
.popup {
  position: fixed;
  top: -50px;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  text-align: center;
  -webkit-transition: opacity 400ms ease-in;
  -moz-transition: opacity 400ms ease-in;
  transition: opacity 400ms ease-in;
}

.popup > div {
  position: relative;
  overflow: auto;
  max-height: min(700px, calc(100vh - 4rem));
  margin: 0 auto;
  border: 1px solid white;
  background-color: white;
  padding: 3rem 7rem;
  border-radius: 8px;
  box-shadow: 0 2px 34px 0 rgba(0, 0, 0, 0.24);
  pointer-events: initial;
  max-width: calc(100vw - 2rem);
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .popup > div {
    padding: 2rem 2rem 3rem;
    max-height: min(700px, calc(100vh - 2rem));
  }
}

@media (max-width: 480px) {
  .popup > div {
    padding: 1.5rem 1rem 2rem;
    max-height: min(700px, calc(100vh - 1rem));
  }
}

.body-popup {
  display: flex;
  justify-content: center;
  min-width: min(420px, calc(100vw - 2rem));
}

@media (max-width: 480px) {
  .body-popup {
    min-width: auto;
  }
}

.popup .closeBt {
  position: absolute;
  right: 2rem;
  top: 2rem;
  font-size: 1.3em;
  font-weight: normal;
  color: var(--grey-46);
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (max-width: 768px) {
  .popup .closeBt {
    right: 1rem;
    top: 1rem;
  }
}

@media (max-width: 480px) {
  .popup .closeBt {
    right: 0.75rem;
    top: 0.75rem;
    font-size: 1.1em;
  }
}
</style>
